import React, { useEffect } from 'react';

import { fetchShipperKpis } from '../../state/features/shippersSlice';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { ApiStatus } from '../../utils/enums';
import { formatCurrency, formatNumber, formatTime } from '../../utils/formattingUtils';
import { useFilters } from '../Filters/useFilters';
import KPIBanner from './KPIBanner';
import KPIShipperList from './KPIShipperList';
import KPICard from './KPICard';

const ShipperKPIs = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const filters = useFilters();

    const KPIApiStatus = useAppSelector((state) => { return state.shippers.KPIApiStatus; });
    const kpis = useAppSelector((state) => { return state.shippers.KPIs; });

    useEffect(() => {
        dispatch(fetchShipperKpis({ filter_criteria: filters }));
    }, [dispatch, filters]);

    const loading = KPIApiStatus === ApiStatus.Idle || KPIApiStatus === ApiStatus.Loading;
    const avgDwellTime = formatTime(
        kpis.netwise_dwell_hours_duration && kpis.freight_order_count ?
            formatNumber(kpis.netwise_dwell_hours_duration / kpis.freight_order_count, 2, 2) :
            null
    );

    const failedStatusMessage = KPIApiStatus === ApiStatus.Failure ? 'Unable to load KPIs' : null;

    return (
        <KPIBanner>
            <KPICard
                title='Total Orders'
                stat={formatNumber(kpis.freight_order_count, 0, 0)}
                loading={loading}
                error={
                    !loading && kpis.freight_order_count === null ?
                        'No orders found' :
                        failedStatusMessage
                }
            >
                <KPIShipperList
                    metric='freight_order_count'
                    metricDisplayName='Orders'
                    formatFn={(shipper): string => {
                        return formatNumber(shipper.freight_order_count, 0, 0);
                    }}
                    noData={kpis.freight_order_count === null}
                />
            </KPICard>
            <KPICard
                title='Total Revenue'
                stat={formatCurrency(kpis.total_revenue_usd_amount)}
                unit='usd'
                loading={loading}
                error={
                    !loading && kpis.total_revenue_usd_amount === null ?
                        'No revenue data found' :
                        failedStatusMessage
                }
            >
                <KPIShipperList
                    metric='total_revenue_usd_amount'
                    metricDisplayName='Revenue'
                    formatFn={(shipper): string => {
                        return formatCurrency(shipper.total_revenue_usd_amount);
                    }}
                    noData={kpis.total_revenue_usd_amount === null}
                />
            </KPICard>
            <KPICard
                title='Dwell'
                stat={avgDwellTime}
                unit='hours : mins'
                loading={loading}
                error={
                    !loading && avgDwellTime === null ?
                        'Dwell is unavailable' :
                        failedStatusMessage
                }
            >
                <KPIShipperList
                    metric='netwise_dwell_hours_duration'
                    metricDisplayName='Dwell'
                    noData={avgDwellTime === null}
                    formatFn={(shipper): string | null => {
                        return formatTime(
                            shipper.netwise_dwell_hours_duration !== null && shipper.freight_order_count ?
                                formatNumber(shipper.netwise_dwell_hours_duration / shipper.freight_order_count, 2, 2) :
                                null
                        );
                    }}
                />
            </KPICard>
            <KPICard
                title='Total Commodities'
                stat={formatNumber(kpis.commodity_count)}
                loading={loading}
                error={
                    !loading && kpis.commodity_count === null ?
                        'No data on commodity counts was found' :
                        failedStatusMessage
                }
            >
                <KPIShipperList
                    metric='commodity_codes'
                    metricDisplayName='Commodities'
                    comparatorFn={(a, b): number => {
                        return b.commodity_codes.length - a.commodity_codes.length;
                    }}
                    formatFn={(shipper): string => {
                        return formatNumber(shipper.commodity_codes.length, 0, 0);
                    }}
                    noData={kpis.commodity_count === null}
                />
            </KPICard>
        </KPIBanner>
    );
};

export default ShipperKPIs;
